import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { useI18n } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { FileText, Search, ExternalLink, Plus, X, Pencil, Trash2 } from "lucide-react";

const catColor = {
  Policies: "sw-badge-gold",
  Compliance: "sw-badge-pending",
  Forms: "sw-badge-neutral",
  Onboarding: "sw-badge-approved",
  Contracts: "sw-badge-rejected",
};

const CATEGORIES = ["Policies", "Compliance", "Forms", "Onboarding", "Contracts", "HR", "Finance", "Other"];

function DocModal({ doc, onClose, onSaved }) {
  const { t } = useI18n();
  const isNew = !doc.id;
  const [form, setForm] = useState({
    title: doc.title || "",
    description: doc.description || "",
    category: doc.category || "Policies",
    url: doc.url || "",
    visible_to_roles: doc.visible_to_roles || [],
  });
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/files/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm({ ...form, url: r.data.url });
    } catch (err) { alert(t("common.upload_failed") + " " + (err?.response?.data?.detail || err.message)); } finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) await api.post("/documents", form);
      else await api.patch(`/documents/${doc.id}`, form);
      onSaved();
    } catch (err) { alert(err?.response?.data?.detail || "Error"); } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[20px] max-w-[520px] w-full p-6 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex justify-between mb-4"><h3 className="sw-heading text-[18px]">{isNew ? "New" : "Edit"} document</h3><button onClick={onClose}><X size={20} /></button></div>
        <div className="grid grid-cols-1 gap-3 mb-4">
          <div><label className="sw-label">Title *</label><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="sw-input" data-testid="doc-title" /></div>
          <div><label className="sw-label">Description</label><textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sw-input" /></div>
          <div><label className="sw-label">Category *</label><select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="sw-input">{CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
          <div>
            <label className="sw-label">{t("docs.upload_url")}</label>
            <input placeholder="https://... or upload below" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} className="sw-input mb-2" />
            <label className="sw-btn-secondary !text-[12px] cursor-pointer inline-flex items-center gap-1.5 w-fit">{uploading ? "..." : t("common.choose_file")}<input type="file" onChange={(e) => e.target.files[0] && upload(e.target.files[0])} className="hidden" disabled={uploading} accept=".pdf,.docx,.doc,.txt,.md,.xlsx,.png,.jpg" data-testid="doc-file" /></label>
            {uploading && <div className="text-[11px] text-[#475569] mt-1">Uploading...</div>}
          </div>
        </div>
        <div className="flex justify-end gap-2"><button onClick={onClose} className="sw-btn-secondary">{t("common.cancel")}</button><button data-testid="save-doc-btn" onClick={save} disabled={saving || !form.title} className="sw-btn-primary">{saving ? "..." : "Save"}</button></div>
      </div>
    </div>
  );
}

export default function Documents() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [editing, setEditing] = useState(null);
  const canManage = ["admin", "hr"].includes(user?.role);

  const load = () => api.get("/documents").then((r) => setDocs(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (d) => {
    if (!window.confirm(t("common.delete_named", { title: d.title }))) return;
    await api.delete(`/documents/${d.id}`);
    load();
  };

  const cats = Array.from(new Set(docs.map((d) => d.category)));
  const filtered = docs.filter((d) => {
    if (cat !== "all" && d.category !== cat) return false;
    if (q && !`${d.title} ${d.description || ""}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-[14px] bg-[#e2e8f0] text-[#475569] flex items-center justify-center"><FileText size={22} /></div>
          <div>
            <h1 className="sw-heading text-[28px]">{t("docs.title")}</h1>
            <p className="text-[14px] text-[#747474]">{filtered.length} {t("docs.subtitle_count")}</p>
          </div>
        </div>
        {canManage && <button data-testid="new-doc-btn" onClick={() => setEditing({})} className="sw-btn-primary"><Plus size={14} /> {t("docs.new_btn")}</button>}
      </div>

      <div className="sw-card flex flex-col lg:flex-row gap-3">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#747474]" />
          <input data-testid="docs-search" value={q} onChange={(e) => setQ(e.target.value)} className="sw-input pl-11" placeholder={t("docs.search")} />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="sw-input lg:w-[220px]">
          <option value="all">{t("directory.all")}</option>
          {cats.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((d) => (
          <div key={d.id} className="sw-card group hover:-translate-y-0.5 hover:border-[#475569] relative">
            <a href={d.url || "#"} target={d.url && d.url !== "#" ? "_blank" : undefined} rel="noreferrer" className="block">
              <div className="flex items-start justify-between mb-3">
                <span className={`sw-badge ${catColor[d.category] || "sw-badge-neutral"}`}>{d.category}</span>
                <ExternalLink size={14} className="text-[#747474] group-hover:text-[#475569]" />
              </div>
              <div className="font-medium text-[16px] text-[#323232] mb-1">{d.title}</div>
              <div className="text-[13px] text-[#747474] leading-relaxed">{d.description}</div>
            </a>
            {canManage && (
              <div className="absolute top-3 right-9 hidden group-hover:flex gap-1 z-10">
                <button data-testid={`edit-doc-${d.id}`} onClick={(e) => { e.stopPropagation(); setEditing(d); }} className="text-[#747474] hover:text-[#475569] bg-white rounded p-1"><Pencil size={12} /></button>
                <button onClick={(e) => { e.stopPropagation(); remove(d); }} className="text-[#747474] hover:text-[#881a27] bg-white rounded p-1"><Trash2 size={12} /></button>
              </div>
            )}
          </div>
        ))}
      </div>

      {editing && <DocModal doc={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}
